import {
  getBrowserName,
  isIOS,
  isStandalonePwa,
} from '@bettermode/common/user-agent'

export type NotificationStatus =
  | 'granted'
  | 'default'
  | 'denied'
  | 'requires-pwa'
  | 'unsupported'

/**
 * The context a notification status occurs in (which browser, or which iOS
 * flow). It drives the help guide today, but can inform other context-specific
 * behavior too.
 */
export type NotificationContext =
  | 'chrome'
  | 'safari'
  | 'firefox'
  | 'edge'
  | 'iosInstall'
  | 'iosSettings'
  | 'generic'

/**
 * Static third-party help links per context. `generic` has no URL (unrecognized
 * browser), so the map is partial and callers look up
 * `NOTIFICATION_GUIDE_URLS[context]`.
 */
export const NOTIFICATION_GUIDE_URLS: Partial<
  Record<NotificationContext, string>
> = {
  chrome: 'https://support.google.com/chrome/answer/3220216',
  safari: 'https://support.apple.com/guide/safari/sfri40734/',
  firefox: 'https://support.mozilla.org/en-US/kb/push-notifications-firefox',
  edge: 'https://support.microsoft.com/en-US/edge/manage-website-notifications-in-microsoft-edge',
  iosInstall: 'https://support.apple.com/guide/iphone/iphea86e5236',
  iosSettings:
    'https://support.apple.com/guide/iphone/change-notification-settings-iph7c3d96bab',
  // generic -> no link
}

/**
 * Injected so the pure status function touches no globals.
 */
export interface NotificationEnv {
  /**
   * `null` means the Notification API is absent (e.g. an iOS Safari tab, where
   * reading `Notification.permission` throws). When the API exists this is
   * always `'default' | 'granted' | 'denied'`, so a separate "has API" flag
   * would be redundant.
   */
  permission: NotificationPermission | null
  isIOS: boolean
  isStandalone: boolean
  /** device-detector client name, used to pick the denied-state context. */
  browserName: string | undefined
}

/**
 * Maps a detected browser name to its notification context for the desktop/
 * non-iOS `denied` case. Unrecognized browsers fall through to `generic` (no
 * link). Edge is checked before Chrome because some Chromium UAs mention both.
 */
const getContextForBrowser = (
  browserName: string | undefined,
): NotificationContext => {
  if (!browserName) {
    return 'generic'
  }

  if (browserName.includes('Edge')) {
    return 'edge'
  }
  if (browserName.includes('Chrome')) {
    return 'chrome'
  }
  if (browserName.includes('Firefox')) {
    return 'firefox'
  }
  if (browserName.includes('Safari')) {
    return 'safari'
  }

  return 'generic'
}

/**
 * Derives the notification status and context from an injected environment.
 * App code calls `readNotificationStatus()` (below); the `__` prefix signals
 * this is internal / test-only.
 */
export const __getNotificationStatus = (
  env: NotificationEnv,
): { status: NotificationStatus; context: NotificationContext | null } => {
  const { permission, isIOS: ios, isStandalone, browserName } = env

  // No Notification API: iOS tabs need the installed PWA; everything else is
  // genuinely unsupported.
  if (permission === null) {
    return ios
      ? { status: 'requires-pwa', context: 'iosInstall' }
      : { status: 'unsupported', context: null }
  }

  // iOS in a regular browser tab: push only works inside the installed PWA.
  if (ios && !isStandalone) {
    return { status: 'requires-pwa', context: 'iosInstall' }
  }

  switch (permission) {
    case 'denied':
      return {
        status: 'denied',
        // Inside an installed iOS PWA the user re-enables via iOS Settings;
        // elsewhere the context depends on the browser.
        context: ios ? 'iosSettings' : getContextForBrowser(browserName),
      }
    case 'granted':
      return { status: 'granted', context: null }
    case 'default':
    default:
      return { status: 'default', context: null }
  }
}

/**
 * Thin wrapper that builds a `NotificationEnv` from real globals + os-utils and
 * delegates to `__getNotificationStatus`. Client-only; guarded for SSR.
 */
export const readNotificationStatus = (): {
  status: NotificationStatus
  context: NotificationContext | null
} =>
  __getNotificationStatus({
    permission: readNotificationPermission(),
    isIOS: isIOS(),
    isStandalone: isStandalonePwa(),
    browserName: getBrowserName(),
  })

const readNotificationPermission = (): NotificationPermission | null => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null
  }

  try {
    return Notification.permission
  } catch {
    return null
  }
}

export type BrowserNotificationMessageId =
  | 'Settings.Notification.BrowserNotifications.Denied'
  | 'Settings.Notification.BrowserNotifications.RequiresPwa'
  | 'Settings.Notification.BrowserNotifications.Unsupported'

export interface BrowserNotificationView {
  /** Toggle is disabled for denied | requires-pwa | unsupported. */
  disabled: boolean
  /** Which Alert message to show, or null when there's nothing to warn about. */
  messageId: BrowserNotificationMessageId | null
  /** Resolved help link; undefined -> render plain text (no anchor). */
  href?: string
}

/**
 * Maps a `(status, context)` pair to the card's presentational view. Part of
 * the module's public surface (consumed by `useBrowserNotificationCard`).
 */
export const getBrowserNotificationView = (
  status: NotificationStatus,
  context: NotificationContext | null,
): BrowserNotificationView => {
  switch (status) {
    case 'denied':
      return {
        disabled: true,
        messageId: 'Settings.Notification.BrowserNotifications.Denied',
        href: context ? NOTIFICATION_GUIDE_URLS[context] : undefined,
      }
    case 'requires-pwa':
      return {
        disabled: true,
        messageId: 'Settings.Notification.BrowserNotifications.RequiresPwa',
        href: context ? NOTIFICATION_GUIDE_URLS[context] : undefined,
      }
    case 'unsupported':
      return {
        disabled: true,
        messageId: 'Settings.Notification.BrowserNotifications.Unsupported',
        href: undefined,
      }
    case 'default':
    case 'granted':
    default:
      return { disabled: false, messageId: null, href: undefined }
  }
}
